import React from 'react';

import {Text} from '../text';
import {ButtonProps} from './types';
import {Spinner} from '../spinner';
import {Layout} from '../layout';
import {Icon} from '../icons';
import {
  ButtonStyleMap,
  ButtonHeightMap,
  ButtonPaddingMap,
  IconPaddingMap,
} from './styles';
import {Badge} from '../badge';
import {Spacer} from '../spacer';
import {useAsyncCallback} from '../../hooks';

export const Button: React.FC<ButtonProps> = ({
  title,
  onPress,
  raised = false,
  spinning = false,
  round = false,
  inline = true,
  kind = 'Primary',
  state = 'Default',
  size = 'Default',
  badge,
  badgeKind = 'Negative',
  leftIconFilled = false,
  rightIconFilled = false,
  leftIcon,
  rightIcon,
}) => {
  const backgroundColor = ButtonStyleMap[kind][state].backgroundColor;
  const textColor = ButtonStyleMap[kind][state].textColor;
  const borderColor = ButtonStyleMap[kind][state].borderColor;
  const borderSize = ButtonStyleMap[kind][state].borderSize;
  const iconReversed = ButtonStyleMap[kind][state].iconReversed;
  const borderRadius = round ? '9999' : '4';
  const padding = ButtonPaddingMap[size];

  const [isRunning, handlePress] = useAsyncCallback(onPress);
  const resolvedSpinning = spinning || isRunning;

  return (
    /** Outer layout with borders and shadows */
    <Layout
      disabled={state === 'Disabled'}
      centered
      hover={kind === 'Clear' ? true : undefined}
      flex={!inline}
      onPress={state !== 'Disabled' ? handlePress : undefined}
      backgroundColor={backgroundColor}
      raised={raised}
      height={ButtonHeightMap[size]}
      width={
        !title && (leftIcon || rightIcon) && round
          ? ButtonHeightMap[size]
          : undefined
      }
      padding={undefined}
      borderStyle={borderSize !== undefined ? 'solid' : undefined}
      borderColor={borderColor}
      borderWidth={borderSize}
      borderRadius={borderRadius}>
      {/** Inner layout with content */}
      <Layout
        horizontal
        flex
        justify={
          !title && (leftIcon || rightIcon) && round
            ? 'center'
            : 'space-between'
        }
        align="center"
        hidden={resolvedSpinning}>
        {/** Left icon (if any) */}
        {leftIcon || !inline ? (
          <Layout
            paddingRight={
              !rightIcon && !title ? IconPaddingMap[size].horizontal : undefined
            }
            paddingLeft={IconPaddingMap[size].horizontal}
            paddingVertical={IconPaddingMap[size].vertical}>
            <Icon
              inverse={iconReversed}
              name={leftIcon || 'edit'}
              transparent={!leftIcon}
              filled={leftIconFilled}
            />
          </Layout>
        ) : null}
        {/** Text (if any) */}
        {title ? (
          <Layout
            flex
            justify="center"
            align="center"
            paddingVertical={
              leftIcon || rightIcon
                ? padding.icon.vertical
                : padding.normal.vertical
            }
            paddingLeft={
              leftIcon ? padding.icon.horizontal : padding.normal.horizontal
            }
            paddingRight={
              rightIcon ? padding.icon.horizontal : padding.normal.horizontal
            }>
            <Text
              align="center"
              textColor={textColor}
              variant="inline_14_emphasis">
              {title}
            </Text>
            {badge && (
              <>
                <Spacer width="6" />
                <Badge
                  content={badge}
                  kind={badgeKind}
                  round={false}
                  size="Small"
                />
              </>
            )}
          </Layout>
        ) : null}
        {/** Right icon (if any) */}
        {rightIcon || !inline ? (
          <Layout
            paddingLeft={
              !leftIcon && !title ? IconPaddingMap[size].horizontal : undefined
            }
            paddingRight={IconPaddingMap[size].horizontal}
            paddingVertical={IconPaddingMap[size].vertical}>
            <Icon
              filled={rightIconFilled}
              inverse={iconReversed}
              name={rightIcon || 'edit'}
              transparent={!rightIcon}
            />
          </Layout>
        ) : null}
      </Layout>
      {resolvedSpinning && (
        <Layout justify="center" align="center" absolute>
          <Spinner kind="Inverse" />
        </Layout>
      )}
    </Layout>
  );
};
